"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface AtcAccordionContextType {
  expandedIds: Set<string>;
  toggleExpanded: (itemId: string, forceState?: boolean) => void;
  expandAll: (allItems: AtcItem[]) => void;
  collapseAll: () => void;
}

const AtcAccordionContext = createContext<AtcAccordionContextType | undefined>(
  undefined,
);

export const AtcAccordionProvider = ({
  initialExpandedIds,
  children,
}: {
  items?: AtcItem[];
  initialExpandedIds?: Set<string>;
  children: ReactNode;
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    initialExpandedIds ?? new Set(),
  );

  const toggleExpanded = useCallback((itemId: string, forceState?: boolean) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (forceState !== undefined) {
        if (forceState) {
          next.add(itemId);
        } else {
          next.delete(itemId);
        }
      } else {
        if (next.has(itemId)) {
          next.delete(itemId);
        } else {
          next.add(itemId);
        }
      }
      return next;
    });
  }, []);

  const getAllItemCodes = useCallback((nodeArray: AtcItem[]): string[] => {
    const codes: string[] = [];
    const traverse = (nodes: AtcItem[]) => {
      nodes.forEach((node) => {
        codes.push(node.code);
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(nodeArray);
    return codes;
  }, []);

  const expandAll = useCallback(
    (allItems: AtcItem[]) => {
      const allCodes = getAllItemCodes(allItems);
      setExpandedIds(new Set(allCodes));
    },
    [getAllItemCodes],
  );

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  return (
    <AtcAccordionContext.Provider
      value={{ expandedIds, toggleExpanded, expandAll, collapseAll }}
    >
      {children}
    </AtcAccordionContext.Provider>
  );
};

export const useAtcAccordion = () => {
  const context = useContext(AtcAccordionContext);
  if (!context) {
    throw new Error("useAtcAccordion must be used within AtcAccordionProvider");
  }
  return context;
};
